import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, ChefHat, BookOpen, Award, 
  ArrowLeft, Loader2, X 
} from 'lucide-react';
import { profileService } from '../../services/profileService';
import { FollowButton } from './FollowButton';

export const ProfileStats = ({ 
  userId, 
  currentUserId,
  initialStats,
  onViewChef 
}) => {
  const [stats, setStats] = useState(initialStats || null);
  const [isLoading, setIsLoading] = useState(!initialStats);
  const [activeModal, setActiveModal] = useState(null); // 'followers' | 'following' | null
  const [modalData, setModalData] = useState([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);

  useEffect(() => {
    if (userId && !initialStats) {
      fetchStats();
    }
  }, [userId, initialStats]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const data = await profileService.getProfileStats(userId);
      setStats(data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const openFollowersModal = async () => {
    setActiveModal('followers');
    setIsLoadingModal(true);
    try {
      const data = await profileService.getFollowers(userId);
      setModalData(data);
    } catch (e) {
      console.error('Error fetching followers:', e);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const openFollowingModal = async () => {
    setActiveModal('following');
    setIsLoadingModal(true);
    try {
      const data = await profileService.getFollowing(userId);
      setModalData(data);
    } catch (e) {
      console.error('Error fetching following:', e);
    } finally {
      setIsLoadingModal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-24 bg-stone-100 dark:bg-stone-800 rounded-[24px] animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {/* Recetas Subidas */}
        <div className="bg-white dark:bg-stone-900 rounded-[24px] border border-stone-100 dark:border-stone-800 p-5 text-center hover:shadow-lg transition-all">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <BookOpen size={24} className="text-primary" />
          </div>
          <p className="text-3xl font-black dark:text-white">{stats?.recipes_uploaded || 0}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mt-1">
            Recetas Subidas
          </p>
        </div>

        {/* Recetas Completadas */}
        <div className="bg-white dark:bg-stone-900 rounded-[24px] border border-stone-100 dark:border-stone-800 p-5 text-center hover:shadow-lg transition-all">
          <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Award size={24} className="text-emerald-500" />
          </div>
          <p className="text-3xl font-black dark:text-white">{stats?.recipes_completed || 0}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mt-1">
            Completadas
          </p>
        </div>

        {/* Seguidores */}
        <button 
          onClick={openFollowersModal}
          className="bg-white dark:bg-stone-900 rounded-[24px] border border-stone-100 dark:border-stone-800 p-5 text-center hover:shadow-lg hover:border-primary/30 transition-all"
        >
          <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Users size={24} className="text-blue-500" />
          </div>
          <p className="text-3xl font-black dark:text-white">{stats?.followers_count || 0}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mt-1">
            Seguidores
          </p>
        </button>

        {/* Siguiendo */}
        <button 
          onClick={openFollowingModal}
          className="bg-white dark:bg-stone-900 rounded-[24px] border border-stone-100 dark:border-stone-800 p-5 text-center hover:shadow-lg hover:border-primary/30 transition-all"
        >
          <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <UserPlus size={24} className="text-amber-500" />
          </div>
          <p className="text-3xl font-black dark:text-white">{stats?.following_count || 0}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-stone-400 mt-1">
            Siguiendo
          </p>
        </button>
      </div>

      {/* Modal for Followers/Following */}
      {activeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-stone-900/60 backdrop-blur-md">
          <div className="bg-white dark:bg-stone-900 w-full max-w-lg max-h-[80vh] rounded-[40px] shadow-2xl animate-in zoom-in-95 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between">
              <h3 className="text-xl font-black heading-serif dark:text-white flex items-center gap-3">
                <Users size={24} className="text-primary" />
                {activeModal === 'followers' ? 'Seguidores' : 'Siguiendo'}
              </h3>
              <button 
                onClick={() => { setActiveModal(null); setModalData([]); }}
                className="p-2 rounded-xl text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingModal ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-primary" size={32} />
                </div>
              ) : modalData.length > 0 ? (
                <div className="space-y-4">
                  {modalData.map(user => (
                    <div 
                      key={user.id}
                      className="bg-stone-50 dark:bg-stone-950 rounded-[24px] p-4 flex items-center gap-4"
                    >
                      <img 
                        src={user.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`}
                        className="w-14 h-14 rounded-2xl object-cover cursor-pointer hover:scale-105 transition-transform"
                        alt={user.display_name}
                        onClick={() => {
                          setActiveModal(null);
                          onViewChef && onViewChef(user.id);
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p 
                          className="font-black text-stone-900 dark:text-white truncate cursor-pointer hover:text-primary transition-colors"
                          onClick={() => {
                            setActiveModal(null);
                            onViewChef && onViewChef(user.id);
                          }}
                        >
                          {user.display_name}
                        </p>
                        <p className="text-sm text-stone-400 truncate">@{user.username}</p>
                        {user.bio && (
                          <p className="text-xs text-stone-500 mt-1 line-clamp-1 italic">"{user.bio}"</p>
                        )}
                      </div>
                      {currentUserId && currentUserId !== user.id && (
                        <FollowButton 
                          chefId={user.id}
                          currentUserId={currentUserId}
                          size="small"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto mb-4 text-stone-200" />
                  <p className="text-stone-500 font-bold">
                    {activeModal === 'followers' 
                      ? 'Aún no tienes seguidores' 
                      : 'Aún no sigues a nadie'}
                  </p>
                  {activeModal === 'following' && (
                    <p className="text-stone-400 text-sm mt-2">
                      Descubre chefs increíbles en la sección "Chefs"
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileStats;
